import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import GroupsIcon from "@mui/icons-material/Groups";
import { config } from "../../../app/utils/config";
import Langs from "../../../app/lang/langs";
import { updateManagersPermissions } from "../settingsSlice";

const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";

const roles = ["RESPCOMPTE", "SDM", "MANAGER"];
const globalPermissions = [
  config.scopes.collabs,
  config.scopes.warnings,
  config.scopes.vcomp,
  config.scopes.ventr,
  config.scopes.medicalVisits,
  config.scopes.opportunities,
  config.scopes.reports,
];
const teamPermissions = [
  config.scopes.shifts,
  config.scopes.leaves,
  config.scopes.groups,
  config.scopes.evaluations,
];

const buildInitialPermissions = () =>
  roles.reduce((acc, role) => {
    acc[role] = {
      global: {
        view: [],
        edit: [],
        remove: [],
      },
      team: {
        view: [],
        edit: [],
      },
    };
    return acc;
  }, {});

export default function MobileManagersPermissionTab() {
  const dispatch = useDispatch();
  const permissions = useSelector((state) => state.settings.managersPermission);

  const [managersPermissions, setManagersPermissions] = useState(buildInitialPermissions);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (permissions) {
      setManagersPermissions(() => {
        const newPermissions = JSON.parse(JSON.stringify(buildInitialPermissions()));
        Object.keys(newPermissions).forEach((role) => {
          if (!permissions[role]) {
            newPermissions[role] = {
              global: {
                [config.permissionTypes.view]: [],
                [config.permissionTypes.edit]: [],
                [config.permissionTypes.remove]: [...globalPermissions],
              },
              team: {
                [config.permissionTypes.view]: [...teamPermissions],
                [config.permissionTypes.edit]: [],
                [config.permissionTypes.remove]: [],
              },
            };
          }
        });
        Object.keys(permissions).forEach((role) => {
          if (!newPermissions[role]) return;
          Object.keys(permissions[role]).forEach((scopeType) => {
            Object.keys(config.permissionTypes).forEach((permissionType) => {
              if (
                permissions[role][scopeType] &&
                permissions[role][scopeType][permissionType]
              ) {
                const validScopes =
                  scopeType === "global" ? globalPermissions : teamPermissions;
                let filteredScopes = permissions[role][scopeType][
                  permissionType
                ].filter((scope) => validScopes.includes(scope));
                if (
                  scopeType === "global" &&
                  permissionType !== config.permissionTypes.view &&
                  permissionType !== config.permissionTypes.edit
                ) {
                  newPermissions[role].global[
                    config.permissionTypes.remove
                  ].push(...filteredScopes);
                  return;
                }
                if (
                  scopeType === "team" &&
                  permissionType !== config.permissionTypes.edit
                ) {
                  newPermissions[role].team[
                    config.permissionTypes.view
                  ].push(...filteredScopes);
                  return;
                }
                newPermissions[role][scopeType][permissionType] = filteredScopes;
              }
            });
          });
          const assignedGlobalScopes = [
            ...newPermissions[role].global[config.permissionTypes.view],
            ...newPermissions[role].global[config.permissionTypes.edit],
          ];
          const unassignedGlobalScopes = globalPermissions.filter(
            (scope) => !assignedGlobalScopes.includes(scope)
          );
          newPermissions[role].global[config.permissionTypes.remove].push(
            ...unassignedGlobalScopes
          );
          const assignedTeamScopes = [
            ...newPermissions[role].team[config.permissionTypes.view],
            ...newPermissions[role].team[config.permissionTypes.edit],
          ];
          const unassignedTeamScopes = teamPermissions.filter(
            (scope) => !assignedTeamScopes.includes(scope)
          );
          newPermissions[role].team[config.permissionTypes.view].push(
            ...unassignedTeamScopes
          );
        });
        return newPermissions;
      });
    }
  }, [permissions]);

  const handlePermissionChange = (role, scopeType, scope, newType) => {
    setManagersPermissions((prevState) => {
      const updatedPermissions = { ...prevState };
      if (!updatedPermissions[role]) {
        updatedPermissions[role] = {
          global: {
            [config.permissionTypes.view]: [],
            [config.permissionTypes.edit]: [],
            [config.permissionTypes.remove]: [],
          },
          team: {
            [config.permissionTypes.view]: [],
            [config.permissionTypes.edit]: [],
            [config.permissionTypes.remove]: [],
          },
        };
      }
      if (!updatedPermissions[role][scopeType]) {
        updatedPermissions[role][scopeType] = {
          [config.permissionTypes.view]: [],
          [config.permissionTypes.edit]: [],
          [config.permissionTypes.remove]: [],
        };
      }
      Object.keys(config.permissionTypes).forEach((type) => {
        const typeKey = config.permissionTypes[type];
        if (!updatedPermissions[role][scopeType][typeKey]) {
          updatedPermissions[role][scopeType][typeKey] = [];
        }
        updatedPermissions[role][scopeType][typeKey] = updatedPermissions[role][
          scopeType
        ][typeKey].filter((item) => item !== scope);
      });
      if (!updatedPermissions[role][scopeType][newType]) {
        updatedPermissions[role][scopeType][newType] = [];
      }
      updatedPermissions[role][scopeType][newType].push(scope);
      return updatedPermissions;
    });
  };

  const handleManagerPermissionsUpdate = (perms) => {
    dispatch(updateManagersPermissions({ managers: perms }));
  };

  const PermissionChip = ({ role, scopeType, scope, type }) => {
    const isActive = managersPermissions[role]?.[scopeType]?.[type]?.includes(scope);

    const iconMap = {
      view: <VisibilityIcon sx={{ fontSize: 14 }} />,
      remove: <VisibilityOffIcon sx={{ fontSize: 14 }} />,
      edit: <EditIcon sx={{ fontSize: 14 }} />,
    };

    const colorMap = {
      view: {
        active: {
          bg: "rgba(76,175,80,0.10)",
          border: "rgba(76,175,80,0.28)",
          text: "#2e7d32",
          shadow: "0 2px 8px rgba(76,175,80,0.15)",
          glow: "0 0 0 3px rgba(76,175,80,0.08)",
        },
        inactive: {
          bg: "rgba(0,0,0,0.02)",
          border: "rgba(0,0,0,0.08)",
          text: "rgba(0,0,0,0.35)",
          shadow: "none",
          glow: "none",
        },
      },
      remove: {
        active: {
          bg: "rgba(211,47,47,0.08)",
          border: "rgba(211,47,47,0.25)",
          text: "#c62828",
          shadow: "0 2px 8px rgba(211,47,47,0.12)",
          glow: "0 0 0 3px rgba(211,47,47,0.06)",
        },
        inactive: {
          bg: "rgba(0,0,0,0.02)",
          border: "rgba(0,0,0,0.08)",
          text: "rgba(0,0,0,0.35)",
          shadow: "none",
          glow: "none",
        },
      },
      edit: {
        active: {
          bg: "rgba(255,144,21,0.10)",
          border: "rgba(255,144,21,0.30)",
          text: "#e07800",
          shadow: "0 2px 8px rgba(255,144,21,0.15)",
          glow: "0 0 0 3px rgba(255,144,21,0.08)",
        },
        inactive: {
          bg: "rgba(0,0,0,0.02)",
          border: "rgba(0,0,0,0.08)",
          text: "rgba(0,0,0,0.35)",
          shadow: "none",
          glow: "none",
        },
      },
    };

    const colors = isActive ? colorMap[type].active : colorMap[type].inactive;
    const labelKey =
      type === "remove" ? "invisible" : type === "view" ? "visible" : type;

    return (
      <Chip
        icon={iconMap[type]}
        label={<Langs str={labelKey} />}
        onClick={() => {
          handlePermissionChange(role, scopeType, scope, type);
          setHasChanges(true);
        }}
        size="small"
        sx={{
          height: 34,
          width: "100%",
          minWidth: 44,
          borderRadius: "10px",
          fontSize: "0.72rem",
          fontWeight: isActive ? 700 : 500,
          letterSpacing: isActive ? "0.02em" : "0",
          bgcolor: colors.bg,
          color: colors.text,
          border: `1.5px solid ${colors.border}`,
          boxShadow: isActive ? `${colors.shadow}, ${colors.glow}` : "none",
          transition: TRANSITION_SPRING,
          cursor: "pointer",
          backdropFilter: isActive ? "blur(8px)" : "none",
          WebkitBackdropFilter: isActive ? "blur(8px)" : "none",
          "& .MuiChip-icon": {
            color: colors.text,
            ml: 0.6,
            transition: TRANSITION_SMOOTH,
          },
          "& .MuiChip-label": {
            px: 0.8,
          },
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: isActive
              ? `${colors.shadow}, ${colors.glow}`
              : "0 2px 6px rgba(0,0,0,0.06)",
          },
          "&:active": {
            transform: "scale(0.95)",
            transition: "all 0.1s ease",
          },
        }}
      />
    );
  };

  /* Role accent colors derived from existing palette */
  const roleAccents = {
    RESPCOMPTE: { hue: "rgba(255,144,21,", gradient: "linear-gradient(135deg, rgba(255,144,21,0.06) 0%, rgba(255,193,0,0.04) 100%)" },
    SDM: { hue: "rgba(0,103,184,", gradient: "linear-gradient(135deg, rgba(0,103,184,0.06) 0%, rgba(46,170,250,0.04) 100%)" },
    MANAGER: { hue: "rgba(0,40,128,", gradient: "linear-gradient(135deg, rgba(0,40,128,0.06) 0%, rgba(0,103,184,0.04) 100%)" },
  };

  const roleHeaderColors = {
    RESPCOMPTE: "#FF8701",
    SDM: "#0067b8",
    MANAGER: "rgb(0, 40, 128)",
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 0,
        pb: "calc(env(safe-area-inset-bottom, 0px) + 100px)",
        px: 1,
        pt: 1.5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {Object.keys(managersPermissions).map((role, roleIdx) => {
          const accent = roleAccents[role] || roleAccents.RESPCOMPTE;
          const headerColor = roleHeaderColors[role] || "#FF8701";

          return (
            <Box
              key={role}
              sx={{
                bgcolor: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderRadius: "18px",
                boxShadow:
                  `0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04), 0 8px 32px ${accent.hue}0.03)`,
                border: `1px solid ${accent.hue}0.08)`,
                overflow: "hidden",
                transition: TRANSITION_SMOOTH,
                animation: `permCardIn 0.4s cubic-bezier(0, 0, 0.2, 1) ${roleIdx * 80}ms both`,
                "@keyframes permCardIn": {
                  from: { opacity: 0, transform: "translateY(12px) scale(0.98)" },
                  to: { opacity: 1, transform: "translateY(0) scale(1)" },
                },
              }}
            >
              {/* Role header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  background: accent.gradient,
                  borderBottom: `1px solid ${accent.hue}0.08)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `${accent.hue}0.10)`,
                    border: `1px solid ${accent.hue}0.12)`,
                    transition: TRANSITION_SMOOTH,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 18, color: headerColor, opacity: 0.85 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: headerColor,
                      letterSpacing: "0.02em",
                      lineHeight: 1.3,
                    }}
                  >
                    {role}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      color: "rgba(0,0,0,0.35)",
                      fontWeight: 500,
                      letterSpacing: "0.03em",
                    }}
                  >
                    {globalPermissions.length + teamPermissions.length} permissions
                  </Typography>
                </Box>
              </Box>

              {/* Global permissions section */}
              <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
                  <SecurityIcon sx={{ fontSize: 13, color: "rgba(0,0,0,0.3)" }} />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      color: "rgba(0,0,0,0.38)",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    <Langs str="GLOBAL_PERMISSIONS" />
                  </Typography>
                </Box>

                {globalPermissions.map((scope, idx) => (
                  <Box key={scope}>
                    <Box
                      sx={{
                        py: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.84rem",
                          fontWeight: 550,
                          color: "rgba(0,0,0,0.78)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        <Langs str={scope} />
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.75, width: "100%" }}>
                        {Object.values(config.permissionTypes).map((type) => (
                          <Box key={type} sx={{ flex: 1, minWidth: 0 }}>
                            <PermissionChip
                              role={role}
                              scopeType="global"
                              scope={scope}
                              type={type}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    {idx < globalPermissions.length - 1 && (
                      <Divider
                        sx={{
                          borderColor: "rgba(0,0,0,0.04)",
                          mx: -0.5,
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>

              {/* Teams permissions section */}
              <Box sx={{ px: 2, pt: 0.5, pb: 2 }}>
                <Divider
                  sx={{
                    mb: 2,
                    borderColor: "rgba(0,0,0,0.06)",
                    borderStyle: "dashed",
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
                  <GroupsIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.3)" }} />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      color: "rgba(0,0,0,0.38)",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    <Langs str="TEAMS_PERMISSIONS" />
                  </Typography>
                </Box>

                {teamPermissions.map((scope, idx) => (
                  <Box key={scope}>
                    <Box
                      sx={{
                        py: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.84rem",
                          fontWeight: 550,
                          color: "rgba(0,0,0,0.78)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        <Langs str={scope} />
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.75, width: "100%" }}>
                        {Object.values(config.permissionTypes)
                          .filter(
                            (type) =>
                              type !== config.permissionTypes.remove
                          )
                          .map((type) => (
                            <Box key={type} sx={{ flex: 1, minWidth: 0 }}>
                              <PermissionChip
                                role={role}
                                scopeType="team"
                                scope={scope}
                                type={type}
                              />
                            </Box>
                          ))}
                      </Box>
                    </Box>
                    {idx < teamPermissions.length - 1 && (
                      <Divider
                        sx={{
                          borderColor: "rgba(0,0,0,0.04)",
                          mx: -0.5,
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Fixed bottom update button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          px: 2,
          pt: 1.5,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          bgcolor: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(24px) saturate(1.8)",
          WebkitBackdropFilter: "blur(24px) saturate(1.8)",
          borderTop: hasChanges
            ? "1px solid rgba(255,144,21,0.15)"
            : "1px solid rgba(0,0,0,0.05)",
          zIndex: 10,
          boxShadow: hasChanges
            ? "0 -2px 20px rgba(255,144,21,0.08), 0 -8px 40px rgba(255,144,21,0.04)"
            : "0 -1px 8px rgba(0,0,0,0.03)",
          transition: TRANSITION_SMOOTH,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          disabled={!hasChanges}
          onClick={() => {
            handleManagerPermissionsUpdate(managersPermissions);
            setHasChanges(false);
          }}
          startIcon={
            <SaveIcon
              sx={{
                transition: TRANSITION_SPRING,
                transform: hasChanges ? "rotate(0deg)" : "rotate(-15deg)",
                opacity: hasChanges ? 1 : 0.5,
              }}
            />
          }
          sx={{
            background: hasChanges
              ? "linear-gradient(135deg, #FFC100 0%, #FF9015 35%, #FF8701 65%, #e07800 100%)"
              : "rgba(0,0,0,0.06)",
            color: hasChanges ? "#fff" : "rgba(0,0,0,0.3)",
            textTransform: "none",
            fontWeight: 650,
            minHeight: 52,
            borderRadius: "14px",
            fontSize: "16px",
            letterSpacing: "0.02em",
            boxShadow: hasChanges
              ? "0 4px 16px rgba(255,144,21,0.30), 0 8px 32px rgba(255,135,1,0.15), inset 0 1px 0 rgba(255,255,255,0.2)"
              : "none",
            transition: TRANSITION_SMOOTH,
            border: hasChanges
              ? "1px solid rgba(255,255,255,0.15)"
              : "1px solid rgba(0,0,0,0.04)",
            "&:hover": {
              background: hasChanges
                ? "linear-gradient(135deg, #FFC100 0%, #FF9015 30%, #FF8701 60%, #d06e00 100%)"
                : "rgba(0,0,0,0.06)",
              boxShadow: hasChanges
                ? "0 6px 24px rgba(255,144,21,0.35), 0 12px 40px rgba(255,135,1,0.18)"
                : "none",
            },
            "&:active": {
              transform: "scale(0.98)",
              transition: "all 0.1s ease",
            },
            "&.Mui-disabled": {
              background: "rgba(0,0,0,0.06)",
              color: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(0,0,0,0.04)",
            },
          }}
        >
          <Langs str="update" />
        </Button>
      </Box>
    </Box>
  );
}
